/**
 * AspectControls — the aspect-overlay pickers ("colour by" + "aspect web")
 * as a workbench primitive. State lives in the store's labels.slice,
 * derivations come from the shared model context, prefs persist via the
 * shared controller hook — this component is only the chrome, in two
 * shapes:
 *
 *   - `variant="hud"`  — the floating desktop pills (select + web dropdown).
 *   - `variant="menu"` — full-width sections for the phone settings menu
 *     (`onPicked` lets the host close the menu after a pick).
 *
 * Renders nothing when the model has no string-valued label keys.
 */
import { type ReactElement } from "react";
import { Waypoints } from "lucide-react";
import { Button, Tooltip } from "@archlang/ui";
import { HudChip } from "../../components/HudChip.js";
import { Popover, PopoverTrigger, PopoverPanel } from "../../components/Popover.js";
import { ColorBySelect } from "../../components/pickers/ColorBySelect.js";
import { useAspectOverlay } from "../../hooks/useAspectOverlay.js";
import { MenuSection } from "../../board/MobileBoardHeader.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { useTranslation } from "../../i18n";

export interface AspectControlsProps {
  readonly variant?: "hud" | "menu";
  /** Called after any pick — the phone menu closes itself with this. */
  readonly onPicked?: () => void;
  /** Render only the "colour by" pill (no aspect-web button) — for hosts that
   *  colour nodes but have no web overlay (the process view header). */
  readonly colorOnly?: boolean;
}

export function AspectControls(props: AspectControlsProps): ReactElement | null {
  const { t } = useTranslation("aspectControls");
  const store = useWorkspace();
  const { model } = useWorkspaceModel();
  const { aspectKeys, aspectColorKey, setAspectColorKey, webKeys, aspectModeOf, setAspectMode } =
    useAspectOverlay(store, model);
  if (aspectKeys.length === 0) return null;

  // Per-key overlay mode: Off / Web (shared hub) / Rep (per-member cylinders).
  // Clicking the active segment turns the key off; picking Rep implies Web.
  const modeSegments = (k: string): ReactElement => {
    const mode = aspectModeOf(k);
    const seg = (m: "web" | "replicated", label: string, title: string): ReactElement => (
      <Tooltip content={title}>
        <Button
          variant={mode === m ? "primary" : "ghost"}
          size="xs"
          aria-pressed={mode === m}
          onClick={() => {
            setAspectMode(k, mode === m ? "off" : m);
            props.onPicked?.();
          }}
        >
          {label}
        </Button>
      </Tooltip>
    );
    return (
      <div className="flex shrink-0 items-center gap-0.5">
        {seg("web", t("webSegment"), t("webSegmentTitle", { key: k }))}
        {seg("replicated", t("repSegment"), t("repSegmentTitle", { key: k }))}
      </div>
    );
  };

  // The colour-by radio: a single choice across all rows (`null` = colour by
  // Type). Its own control so a row's Web/Rep buttons don't also flip colour.
  const colorRadio = (key: string | null, label: string): ReactElement => {
    const on = aspectColorKey === key;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        title={t("colorRowTitle", { key: label })}
        onClick={() => {
          setAspectColorKey(key);
          props.onPicked?.();
        }}
        className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
      >
        <span
          aria-hidden="true"
          className={`h-3 w-3 shrink-0 rounded-full border ${
            on ? "border-violet-400 bg-violet-500" : "border-slate-600"
          }`}
        />
        <span className="truncate text-slate-200">{label}</span>
      </button>
    );
  };

  if (props.variant === "menu") {
    return (
      <>
        <MenuSection title={t("coloringSection")}>
          <div className="relative">
            <select
              value={aspectColorKey ?? ""}
              onChange={(e) => {
                setAspectColorKey(e.target.value || null);
                props.onPicked?.();
              }}
              className="w-full appearance-none rounded-2xl bg-white/[0.06] py-3 pl-4 pr-10 text-[15px] text-slate-200 transition-colors active:bg-white/10"
            >
              <option value="">{t("colorByTypeOption")}</option>
              {aspectKeys.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
            >
              ▾
            </span>
          </div>
        </MenuSection>
        <MenuSection title={t("aspectOverlaySection")}>
          <div className="divide-y divide-white/5 overflow-hidden rounded-2xl bg-white/[0.06]">
            {aspectKeys.map((k) => (
              <div
                key={k}
                className="flex items-center justify-between gap-3 px-4 py-3 text-[15px] text-slate-200"
              >
                <span className="truncate">{k}</span>
                {modeSegments(k)}
              </div>
            ))}
          </div>
        </MenuSection>
      </>
    );
  }

  // The process-view header colours nodes but has no web overlay — it keeps the
  // standalone colour-by select (also used by the view builder).
  if (props.colorOnly) {
    return (
      <ColorBySelect
        aspectKeys={aspectKeys}
        value={aspectColorKey}
        onChange={(k) => setAspectColorKey(k)}
        data-testid="color-by-pill"
      />
    );
  }

  // Board HUD: ONE pill opening a single menu that owns the whole aspect-key
  // axis — a colour-by radio column (Type first, then each key) beside the
  // Web/Rep overlay toggles. Type has no overlay (colouring by type isn't an
  // aspect), so its overlay cell is a skipped "—".
  const overlayActive = webKeys.size > 0;
  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        {({ ref, props }) => (
          <Tooltip content={t("menuButtonTitle")}>
            <HudChip
              ref={ref}
              {...props}
              data-testid="aspect-menu-pill"
              active={aspectColorKey !== null || overlayActive}
              accent="violet"
            >
              <Waypoints width={13} height={13} aria-hidden="true" className="shrink-0" />
              <span>{aspectColorKey ?? t("aspectsLabel")}</span>
              {overlayActive && <span className="opacity-70">({webKeys.size})</span>}
              <span className="opacity-60">▾</span>
            </HudChip>
          </Tooltip>
        )}
      </PopoverTrigger>
      <PopoverPanel
        data-web-menu=""
        className="z-overlay max-h-72 min-w-56 overflow-auto rounded-lg border border-slate-700 bg-slate-900/(--tint-solid) p-1 shadow-xl backdrop-blur-md"
      >
        <div className="flex items-center justify-between gap-3 px-2 pb-1 pt-0.5 text-[10px] uppercase tracking-wide text-slate-500">
          <span>{t("coloringSection")}</span>
          <span>{t("aspectOverlaySection")}</span>
        </div>
        <div role="radiogroup" aria-label={t("coloringSection")}>
          {/* Type = the default (no aspect) colouring; no overlay. */}
          <div className="flex items-center justify-between gap-3 rounded-sm px-2 py-1 text-xs">
            {colorRadio(null, t("colorByTypeOption"))}
            <span aria-hidden="true" className="shrink-0 px-2 text-slate-600">
              —
            </span>
          </div>
          {aspectKeys.map((k) => (
            <div key={k} className="flex items-center justify-between gap-3 rounded-sm px-2 py-1 text-xs">
              {colorRadio(k, k)}
              {modeSegments(k)}
            </div>
          ))}
        </div>
      </PopoverPanel>
    </Popover>
  );
}
